import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Languages,
  Subtitles,
  Speech,
  Eye,
  EyeOff,
  Mic,
  GraduationCap,
  MessageCircle,
  Quote,
  Loader2,
  Sparkles,
  Lightbulb,
  Check,
  ClipboardCopy,
  Plus,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { AnimatePresence, motion } from "framer-motion";
import { cn } from "@/lib/utils";
import AudioWave from "@/components/ui/audio-wave";
import { ChartRadialText } from "@/components/ui/chart-radial-text";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { TextDiffHighlight } from "@/lib/text-diff-highlight-improved";
import { normalizeChosenExplanationText } from "@/lib/vocabulary/chosen-explanation";
import { calculateOptimalSimilarity } from "@/lib/text-similarity";
import { calculateTextDiff, DiffType } from "@/lib/text-diff-highlight-improved";
import { useUser } from "@/lib/context/user-context";
import { useI18n } from "@/lib/i18n";
import { usePlayer } from "@/contexts/player-context";
import { hasEpisodeFeatureAccess } from "@/lib/feature-access";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SpeakerIcon } from "@/components/SpeakerIcon";
import { useYoudaoTranslation } from "@/hooks/use-youdao-translation";
import { useWordExplanation } from "@/hooks/use-word-explanation";
import { vocabularyLevelChecker } from "@/lib/vocabularyLevel";
import { getSpeakerLang } from "@/utils/device-detection";
import { UserSettings } from "@/contexts/user-settings-context";
import { NarrationModeIndicatorCard } from "@/components/episode/NarrationModeIndicatorCard";

const PANEL_WRAPPER_CLASS =
  "pointer-events-none fixed right-3 md:right-[24px] sm:right-12 top-[450px] md:top-[720px] sm:top-[400px] z-50";
const PANEL_INNER_CLASS =
  "pointer-events-auto flex flex-col gap-2 rounded-3xl border border-border/60 bg-background/70 p-1 shadow-2xl dark:border-slate-800/60 dark:bg-white/30";
const ICON_BUTTON_BASE_CLASS =
  "group relative h-9 w-9 sm:h-10 sm:w-10 rounded-2xl border transition-all duration-200 shadow-sm hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 supports-[backdrop-filter]:backdrop-blur-md";
const ICON_BUTTON_INACTIVE_CLASS =
  "border-border/70 bg-background/80 text-muted-foreground hover:text-foreground hover:bg-background/90 dark:border-border/50 dark:bg-white/30 dark:text-slate-200/80 dark:hover:bg-slate-800/80 dark:hover:text-slate-50";
const ICON_BUTTON_ACTIVE_CLASS =
  "border-primary/80 bg-primary text-primary-foreground shadow-[0_10px_24px_-12px_rgba(59,130,246,0.65)] hover:bg-primary/95 hover:text-primary-foreground dark:border-primary/60 dark:shadow-[0_14px_30px_-12px_rgba(56,189,248,0.8)]";
const ICON_BUTTON_SUBTLE_CLASS =
  "hover:bg-background/85 dark:hover:bg-slate-900/75";
const TEXT_TOGGLE_BUTTON_CLASS =
  "group relative flex items-center gap-2 rounded-2xl border px-2 py-1.5 text-xs font-medium shadow-sm transition-all duration-200 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 supports-[backdrop-filter]:backdrop-blur-md sm:text-sm";
const TEXT_TOGGLE_INACTIVE_CLASS =
  "border-border/70 bg-background/80 text-muted-foreground hover:bg-muted/70 dark:border-border/50 dark:bg-white/30 dark:text-slate-200/80 dark:hover:bg-slate-800/75";
const TEXT_TOGGLE_ACTIVE_CLASS =
  "border-primary/80 bg-primary text-primary-foreground shadow-[0_10px_24px_-12px_rgba(59,130,246,0.6)] hover:bg-primary/95 hover:text-primary-foreground dark:border-primary/60 dark:shadow-[0_14px_30px_-12px_rgba(56,189,248,0.75)]";
const MANUAL_BUTTON_WRAPPER_CLASS =
  "pointer-events-auto fixed right-[18px] md:right-[30px] top-[680px] md:top-[970px] sm:right-[54px] sm:top-[720px] z-50";
const COLLAPSE_BUTTON_CLASS =
  "group relative h-9 w-9 sm:h-10 sm:w-10 rounded-2xl border border-border/60 bg-background/85 text-muted-foreground shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:text-foreground hover:bg-background focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 supports-[backdrop-filter]:backdrop-blur-md dark:border-border/50 dark:bg-white/30 dark:text-slate-200/85 dark:hover:bg-slate-800/70";
const COLLAPSE_BUTTON_ACTIVE_CLASS =
  "border-primary/80 bg-primary text-primary-foreground hover:bg-primary/90";

const STATUS_DEFINITIONS = [
  {
    value: 'NEW' as const,
    labelKey: 'mobileImmersive.status.new',
    descriptionKey: 'mobileImmersive.status.newDescription',
    fallbackLabel: 'New',
    fallbackDescription: 'Just added, needs first review',
  },
  {
    value: 'FUZZY' as const,
    labelKey: 'mobileImmersive.status.fuzzy',
    descriptionKey: 'mobileImmersive.status.fuzzyDescription',
    fallbackLabel: 'Fuzzy',
    fallbackDescription: 'Needs more practice soon',
  },
  {
    value: 'MASTERED' as const,
    labelKey: 'mobileImmersive.status.mastered',
    descriptionKey: 'mobileImmersive.status.masteredDescription',
    fallbackLabel: 'Mastered',
    fallbackDescription: 'Confident and on long review',
  },
] as const;

type StatusValue = typeof STATUS_DEFINITIONS[number]['value'];

interface StatusOption {
  value: StatusValue;
  label: string;
  description: string;
}

const statusStyles: Record<StatusValue, string> = {
  NEW: 'bg-sky-100 text-sky-700 border-sky-200',
  FUZZY: 'bg-amber-100 text-amber-700 border-amber-200',
  MASTERED: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

const isPlayableNarrationAudio = (url?: string | null) => {
  if (typeof url !== "string") {
    return false;
  }

  const trimmed = url.trim();
  if (!trimmed) {
    return false;
  }

  const basePath = trimmed.split("?")[0] ?? trimmed;
  const extension = basePath.split(".").pop()?.toLowerCase();
  if (!extension) {
    return true;
  }

  if (extension === "l16" || extension === "pcm") {
    return false;
  }

  return true;
};

const statusAccentMap: Record<StatusValue, string> = {
  NEW: 'bg-sky-400',
  FUZZY: 'bg-amber-400',
  MASTERED: 'bg-emerald-400',
};

// 100ms 静音音频，用于在用户手势中预热音频播放权限
const SILENT_WAV_DATA_URI =
  'data:audio/wav;base64,UklGRmQGAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YUAGAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';

interface MobileImmersiveFloatingButtonsProps {
  showSubtitles: boolean;
  setShowSubtitles: (show: boolean) => void;
  showTranslation: boolean;
  setShowTranslation: (show: boolean) => void;
  allTranslationsRevealed: boolean;
  setAllTranslationsRevealed: (value: boolean) => void;
  sentence?: string;
  translation?: string | Record<string, string>;
  subtitleTimestamp?: [number, number];
  currentTime?: number;
  onExitImmersive: () => void;
  episodeId?: string;
  episodeTitle?: string;
  episodeThumbnail?: string;
  activeSubtitleId?: string | null;
  userSettings?: UserSettings;
  session?: any;
  activeVocabularyTerms?: string[];
  vocabulary?: any;
  onVocabularyUpdate?: (updatedVocabulary: any) => void;
}

export const MobileImmersiveFloatingButtons: React.FC<MobileImmersiveFloatingButtonsProps> = ({
  showSubtitles,
  setShowSubtitles,
  showTranslation,
  setShowTranslation,
  allTranslationsRevealed,
  setAllTranslationsRevealed,
  sentence,
  translation,
  subtitleTimestamp,
  currentTime,
  onExitImmersive,
  episodeId,
  episodeTitle,
  episodeThumbnail,
  activeSubtitleId,
  userSettings,
  session,
  activeVocabularyTerms,
  vocabulary,
  onVocabularyUpdate,
}) => {
  void onExitImmersive;
  
  const displayedTranslation = useMemo(() => {
    if (!translation) return '';
    if (typeof translation === 'string') return translation;
    if (typeof translation === 'object') {
      const lang = userSettings?.languageSetting?.split('-')[0]?.toLowerCase() || 'zh';
      return translation[lang] || translation['zh'] || Object.values(translation)[0] || '';
    }
    return '';
  }, [translation, userSettings]);

  const {
    isRecording,
    startRecording,
    stopRecording,
    isTranscribing,
    transcriptionError,
    audioUrl,
    audioLevel,
    transcribedText,
    resetRecorder,
  } = useAudioRecorder();
  const startTimeRef = useRef<number | null>(null);
  const effectiveLevel = Math.max(0, audioLevel ?? 0);
  const [shadowDialogOpen, setShadowDialogOpen] = useState(false);
  const [shadowPracticeMode, setShadowPracticeMode] = useState(false);
  const [manualShadowing, setManualShadowing] = useState(false);
  const [dialogShowSentence, setDialogShowSentence] = useState(true);
  const [dialogShowTranslation, setDialogShowTranslation] = useState(false);
  const [attemptCount, setAttemptCount] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [narrationEnabled, setNarrationEnabled] = useState(false);
  const [isNarrationProcessing, setIsNarrationProcessing] = useState(false);
  const [isNarrationPlaying, setIsNarrationPlaying] = useState(false);
  const [isNarrationPaused, setIsNarrationPaused] = useState(false);
  const [currentNarrationTerms, setCurrentNarrationTerms] = useState<string[]>([]);
  const [isNarrationCardPressed, setIsNarrationCardPressed] = useState(false);
  const hasSentence = Boolean(sentence && sentence.trim().length > 0);
  const { user } = useUser();
  const hasNarrationFeatureAccess = hasEpisodeFeatureAccess(user);
  const { t, lang } = useI18n();
  const previousSubtitleIdRef = useRef<string | null>(null);
  const triggeredSubtitleIdRef = useRef<string | null>(null);
  const manualSubtitleIdRef = useRef<string | null>(null);
  const shouldResumePlaybackRef = useRef(true);
  const wasDialogOpenRef = useRef(false);
  const { pause, play, acquirePlaybackLock } = usePlayer();
  const speakerRef = useRef<HTMLDivElement>(null);
  const lastNarratedSubtitleRef = useRef<string | null>(null);
  const narrationInFlightRef = useRef(false);
  const narrationAudioRef = useRef<HTMLAudioElement | null>(null);
  const narrationEnabledRef = useRef(narrationEnabled);
  const narrationLockReleaseRef = useRef<(() => void) | null>(null);
  const narrationAudioUnlockedRef = useRef(false);
  const previousUserSelectRef = useRef<{
    userSelect?: string;
    webkitUserSelect?: string;
    msUserSelect?: string;
  } | null>(null);
  const primaryNarrationTerm = currentNarrationTerms[0];
  const narrationCardSurfaceClass = isNarrationCardPressed
    ? 'border-transparent bg-transparent backdrop-blur-none shadow-none'
    : 'border border-border/40 bg-foreground/70 backdrop-blur-sm shadow-2xl dark:border-white/20 dark:bg-slate-900/20';
  const narrationCardContentFadeClass = isNarrationCardPressed
    ? 'text-transparent [&_*]:text-transparent [&_*]:caret-transparent'
    : undefined;

  const ensureNarrationAudioElement = useCallback(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    if (narrationAudioRef.current) {
      return narrationAudioRef.current;
    }

    const audio = new Audio();
    audio.preload = 'auto';
    audio.crossOrigin = 'anonymous';
    audio.setAttribute('playsinline', 'true');
    audio.setAttribute('webkit-playsinline', 'true');
    narrationAudioRef.current = audio;
    return audio;
  }, []);

  const primeNarrationAudioElement = useCallback(async () => {
    if (narrationAudioUnlockedRef.current) {
      return;
    }

    const audio = ensureNarrationAudioElement();
    if (!audio) {
      return;
    }

    try {
      audio.muted = true;
      audio.src = SILENT_WAV_DATA_URI;
      await audio.play();
      audio.pause();
      audio.currentTime = 0;
      audio.removeAttribute('src');
      if (typeof audio.load === 'function') {
        audio.load();
      }
      narrationAudioUnlockedRef.current = true;
    } catch (error) {
      console.warn('Failed to prime narration audio element:', error);
    } finally {
      audio.muted = false;
    }
  }, [ensureNarrationAudioElement]);

  const handleNarrationToggle = useCallback(async () => {
    if (!hasNarrationFeatureAccess) {
      return;
    }

    const next = !narrationEnabledRef.current;
    setNarrationEnabled(next);

    if (next) {
      await primeNarrationAudioElement();
    }
  }, [hasNarrationFeatureAccess, primeNarrationAudioElement]);

  const disableGlobalSelection = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }

    if (previousUserSelectRef.current) {
      return;
    }

    const bodyStyle = document.body.style as any;
    previousUserSelectRef.current = {
      userSelect: bodyStyle.userSelect,
      webkitUserSelect: bodyStyle.webkitUserSelect,
      msUserSelect: bodyStyle.msUserSelect,
    };

    bodyStyle.userSelect = 'none';
    bodyStyle.webkitUserSelect = 'none';
    bodyStyle.msUserSelect = 'none';
  }, []);

  const restoreGlobalSelection = useCallback(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const previous = previousUserSelectRef.current;
    if (!previous) {
      return;
    }

    const bodyStyle = document.body.style as any;
    bodyStyle.userSelect = previous.userSelect ?? '';
    bodyStyle.webkitUserSelect = previous.webkitUserSelect ?? '';
    bodyStyle.msUserSelect = previous.msUserSelect ?? '';
    previousUserSelectRef.current = null;
  }, []);

  // useEffect(() => {
  //   if (typeof window === 'undefined') {
  //     return;
  //   }

  //   const handleDrawerOpenChange = (event: Event) => {
  //     const customEvent = event as CustomEvent<{ open: boolean }>;
  //     setIsExternalNarrationOverlayActive(Boolean(customEvent.detail?.open));
  //   };

  //   window.addEventListener(
  //     'skiper-drawer-open-change',
  //     handleDrawerOpenChange
  //   );

  //   if (typeof document !== 'undefined') {
  //     setIsExternalNarrationOverlayActive(
  //       document.body.classList.contains('skiper-drawer-open')
  //     );
  //   }

  //   return () => {
  //     window.removeEventListener(
  //       'skiper-drawer-open-change',
  //       handleDrawerOpenChange
  //     );
  //   };
  // }, []);

  useEffect(() => {
    if (!hasNarrationFeatureAccess && narrationEnabledRef.current) {
      narrationEnabledRef.current = false;
      setNarrationEnabled(false);
    }
  }, [hasNarrationFeatureAccess]);

  useEffect(() => {
    narrationEnabledRef.current = narrationEnabled;
    if (!narrationEnabled) {
      setIsNarrationPlaying(false);
      setIsNarrationPaused(false);
      setCurrentNarrationTerms([]);
      setIsNarrationCardPressed(false);
      restoreGlobalSelection();
    } else {
      setIsNarrationPaused(false);
    }

    if (!narrationEnabled && narrationAudioRef.current) {
      const audio = narrationAudioRef.current;
      audio.pause();
      audio.currentTime = 0;
      audio.removeAttribute('src');
      if (typeof audio.load === 'function') {
        audio.load();
      }
    }
  }, [narrationEnabled, restoreGlobalSelection]);

  useEffect(() => {
    if (!isNarrationPlaying) {
      setIsNarrationCardPressed(false);
      restoreGlobalSelection();
    }

    return restoreGlobalSelection;
  }, [isNarrationPlaying, restoreGlobalSelection]);

  const findVocabularyTerm = useCallback(
    (targetTerm: string) => {
      if (!vocabulary?.scenarios) {
        return null;
      }

      const normalizedTarget = targetTerm.trim().toLowerCase();
      for (let scenarioIndex = 0; scenarioIndex < vocabulary.scenarios.length; scenarioIndex++) {
        const scenario = vocabulary.scenarios[scenarioIndex];
        const terms = Array.isArray(scenario?.terms) ? scenario.terms : [];
        for (let termIndex = 0; termIndex < terms.length; termIndex++) {
          const termEntry = terms[termIndex];
          if (typeof termEntry?.term !== 'string') {
            continue;
          }
          if (termEntry.term.trim().toLowerCase() === normalizedTarget) {
            return { scenarioIndex, termIndex, scenario, term: termEntry };
          }
        }
      }

      return null;
    },
    [vocabulary]
  );
  const [selectedWord, setSelectedWord] = useState("");
  const [selectedWordContext, setSelectedWordContext] = useState("");
  const [showWordDrawer, setShowWordDrawer] = useState(false);
  const [selectedStatusValue, setSelectedStatusValue] = useState<StatusValue>("NEW");
  const lastTapRef = useRef(0);

  const {
    translations: youdaoTranslations,
    loading: youdaoLoading,
    getTranslation: fetchYoudaoTranslation,
    isInitialized: isYoudaoInitialized,
  } = useYoudaoTranslation();

  const {
    explanations,
    loading: explanationLoading,
    savingWord,
    getExplanation: fetchExplanation,
    saveCard,
    isCardSaved,
  } = useWordExplanation(userSettings, selectedWordContext, session?.user?.id);

  const statusOptions = useMemo<StatusOption[]>(() =>
    STATUS_DEFINITIONS.map((definition) => ({
      value: definition.value,
      label: t(definition.labelKey, definition.fallbackLabel),
      description: t(definition.descriptionKey, definition.fallbackDescription),
    })),
  [t]);

  const selectedStatusMobile = useMemo<StatusOption>(() => {
    const fallback: StatusOption = statusOptions[0] ?? {
      value: 'NEW',
      label: t('mobileImmersive.status.new', 'New'),
      description: t('mobileImmersive.status.newDescription', 'Just added, needs first review'),
    };
    return statusOptions.find((option) => option.value === selectedStatusValue) ?? fallback;
  }, [statusOptions, selectedStatusValue, t]);

  const wordExplanation = selectedWord ? explanations[selectedWord] : undefined;
  const wordTranslation = selectedWord ? youdaoTranslations[selectedWord] : undefined;
  const isWordLoading = selectedWord
    ? Boolean(explanationLoading[selectedWord] || youdaoLoading[selectedWord])
    : false;
  const isWordSaving = selectedWord ? Boolean(savingWord[selectedWord]) : false;
  const isWordSaved = selectedWord ? isCardSaved(selectedWord) : false;
  const StatusIconMobile =
    selectedStatusMobile.value === 'MASTERED'
      ? Check
      : selectedStatusMobile.value === 'FUZZY'
      ? Lightbulb
      : Sparkles;
  const shadowingAccuracy = useMemo(() => {
    if (!sentence || !transcribedText) {
      return null;
    }

    const cleanedSentence = sentence.trim();
    if (!cleanedSentence) {
      return null;
    }

    return calculateOptimalSimilarity(cleanedSentence, transcribedText);
  }, [sentence, transcribedText]);

  const handleWordDrawerOpen = useCallback(
    async (word: string, contextText: string) => {
      const trimmed = word.trim();
      if (!trimmed) {
        return;
      }

      setSelectedWord(trimmed);
      setSelectedWordContext(contextText);
      setSelectedStatusValue('NEW');
      setShowWordDrawer(true);

      let hasLocal = false;
      try {
        hasLocal = await vocabularyLevelChecker.hasTranslationAsync(trimmed.toLowerCase());
      } catch (error) {
        console.warn('Failed to check local vocabulary:', trimmed, error);
      }

      try {
        await fetchExplanation(trimmed);
      } catch (error) {
        console.error('Failed to fetch explanation:', trimmed, error);
      }

      if (!hasLocal && isYoudaoInitialized) {
        try {
          await fetchYoudaoTranslation(trimmed);
        } catch (error) {
          console.error('Failed to fetch Youdao translation:', trimmed, error);
        }
      }
    },
    [fetchExplanation, fetchYoudaoTranslation, isYoudaoInitialized]
  );

  const skipNarration = useCallback(() => {
    console.log('⏭️ 用户跳过讲解');

    // 停止当前播放的音频
    if (narrationAudioRef.current) {
      narrationAudioRef.current.pause();
      narrationAudioRef.current.currentTime = 0;
      narrationAudioRef.current.removeAttribute('src');
      if (typeof narrationAudioRef.current.load === 'function') {
        narrationAudioRef.current.load();
      }
    }

    // 关闭讲解界面
    setIsNarrationPlaying(false);
    setIsNarrationPaused(false);
    setCurrentNarrationTerms([]);
    setIsNarrationCardPressed(false);
    restoreGlobalSelection();

    // 释放 playback lock
    if (narrationLockReleaseRef.current) {
      narrationLockReleaseRef.current();
      narrationLockReleaseRef.current = null;
    }

    // 清理状态
    narrationInFlightRef.current = false;
    setIsNarrationProcessing(false);

    // 🔥 关键：标记当前字幕已处理，防止重复触发
    if (activeSubtitleId) {
      lastNarratedSubtitleRef.current = activeSubtitleId;
      console.log('✅ 标记字幕已处理:', activeSubtitleId);
    }

    // 恢复视频播放
    play();
  }, [play, activeSubtitleId, restoreGlobalSelection]);

  type NarrationAudioQueueItem = {
    term?: string;
    url: string;
  };

  const playNarrationQueue = useCallback(async (items: NarrationAudioQueueItem[]) => {
    if (!items.length || !narrationEnabledRef.current) {
      return false;
    }

    if (!narrationAudioUnlockedRef.current) {
      await primeNarrationAudioElement();
    }

    const audio = ensureNarrationAudioElement();
    if (!audio) {
      return false;
    }

    let hasSuccessfulPlayback = false;

    // 先关闭再打开，以重置 AIVoiceInput 组件的时间
    setIsNarrationPlaying(false);
    setIsNarrationPaused(false);
    await new Promise(resolve => setTimeout(resolve, 50));
    setIsNarrationPlaying(true);
    setIsNarrationPaused(false);

    for (const { term, url } of items) {
      if (!url || !narrationEnabledRef.current) {
        break;
      }

      if (term) {
        setCurrentNarrationTerms([term]);
      }

      try {
        // eslint-disable-next-line no-await-in-loop
        await new Promise<void>((resolve) => {
          let finished = false;

          const cleanup = () => {
            if (finished) {
              return;
            }
            finished = true;
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('error', handleError);
            resolve();
          };

          const handleEnded = () => cleanup();
          const handleError = (event?: Event) => {
            console.warn('Narration audio failed to play:', url, event);
            cleanup();
          };

          audio.addEventListener('ended', handleEnded);
          audio.addEventListener('error', handleError);
          audio.currentTime = 0;
          audio.src = url;
          audio.muted = false;

          const playPromise = audio.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise
              .then(() => {
                hasSuccessfulPlayback = true;
              })
              .catch((error) => {
                console.error('Narration playback error:', error);
                handleError();
              });
          } else {
            hasSuccessfulPlayback = true;
          }
        });
      } catch (error) {
        console.error('Failed during narration playback sequence:', error);
      }
    }

    audio.pause();
    audio.currentTime = 0;
    audio.removeAttribute('src');
    if (typeof audio.load === 'function') {
      audio.load();
    }

    setIsNarrationPlaying(false);
    setIsNarrationPaused(false);
    setCurrentNarrationTerms([]);
    setIsNarrationCardPressed(false);
    restoreGlobalSelection();

    return hasSuccessfulPlayback;
  }, [ensureNarrationAudioElement, primeNarrationAudioElement, restoreGlobalSelection]);

  const handleNarrationPlaybackToggle = useCallback(() => {
    const audio = narrationAudioRef.current;
    if (!audio || !audio.src) {
      return;
    }

    if (audio.paused) {
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => setIsNarrationPaused(false))
          .catch((error) => {
            console.warn('Failed to resume narration playback:', error);
          });
      } else {
        setIsNarrationPaused(false);
      }
    } else {
      audio.pause();
      setIsNarrationPaused(true);
    }
  }, []);

  const handleNarrationReplay = useCallback(() => {
    const audio = narrationAudioRef.current;
    if (!audio || !audio.src) {
      return;
    }

    try {
      audio.pause();
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.then === 'function') {
        playPromise
          .then(() => setIsNarrationPaused(false))
          .catch((error) => {
            console.warn('Failed to replay narration:', error);
          });
      } else {
        setIsNarrationPaused(false);
      }
    } catch (error) {
      console.warn('Failed to replay narration:', error);
    }
  }, []);

  useEffect(() => {
    resetRecorder();
    setAttemptCount(0);
  }, [sentence, activeSubtitleId, resetRecorder]);

  // 添加 ESC 键监听，用于跳过讲解
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isNarrationPlaying) {
        skipNarration();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isNarrationPlaying, skipNarration]);

  useEffect(() => {
    if (!narrationEnabled) {
      return;
    }

    if (!sentence || !sentence.trim()) {
      return;
    }

    if (!subtitleTimestamp || !Array.isArray(subtitleTimestamp)) {
      return;
    }

    if (typeof currentTime !== 'number') {
      return;
    }

    if (!activeSubtitleId) {
      return;
    }

    const startTime = typeof subtitleTimestamp[0] === 'number' ? subtitleTimestamp[0] : undefined;
    const endTime = typeof subtitleTimestamp[1] === 'number' ? subtitleTimestamp[1] : startTime;
    if (typeof endTime !== 'number') {
      return;
    }

    const offset = 0.25;
    const triggerPoint = Math.max(startTime ?? endTime, endTime - offset);

    if (currentTime < triggerPoint) {
      return;
    }

    if (lastNarratedSubtitleRef.current === activeSubtitleId) {
      return;
    }

    if (narrationInFlightRef.current) {
      return;
    }

    const sentenceLower = sentence.toLowerCase();
    const uniqueTerms = Array.from(new Set((activeVocabularyTerms ?? []).map((term) => term?.trim()).filter(Boolean)));

    if (uniqueTerms.length === 0) {
      setCurrentNarrationTerms([]);
      setIsNarrationPaused(false);
      setIsNarrationCardPressed(false);
      restoreGlobalSelection();
      lastNarratedSubtitleRef.current = activeSubtitleId;
      return;
    }

    const narrationTargets = uniqueTerms
      .map((termName) => {
        if (!termName) {
          return null;
        }

        const normalizedName = termName.toLowerCase();
        if (!sentenceLower.includes(normalizedName)) {
          return null;
        }

        const match = findVocabularyTerm(termName);
        if (!match) {
          return null;
        }

        const chosenExplanation = normalizeChosenExplanationText(
          match.term?.chosen_explanation,
          userSettings?.languageSetting?.split('-')[0]?.toLowerCase()
        );
        if (!chosenExplanation) {
          return null;
        }

        return {
          scenarioIndex: match.scenarioIndex,
          termIndex: match.termIndex,
          term: match.term.term,
          chosenExplanation,
          chosenExplanationAudio: match.term?.chosen_explanation_audio as string | undefined,
        };
      })
      .filter((item): item is {
        scenarioIndex: number;
        termIndex: number;
        term: string;
        chosenExplanation: string;
        chosenExplanationAudio: string | undefined;
      } => Boolean(item));

    if (narrationTargets.length === 0) {
      setCurrentNarrationTerms([]);
      setIsNarrationPaused(false);
      setIsNarrationCardPressed(false);
      restoreGlobalSelection();
      lastNarratedSubtitleRef.current = activeSubtitleId;
      return;
    }

    setCurrentNarrationTerms(narrationTargets.map((item) => item.term));

    const audioToPlay = narrationTargets
      .filter((item) => isPlayableNarrationAudio(item.chosenExplanationAudio))
      .map((item) => ({
        term: item.term,
        url: item.chosenExplanationAudio!.trim(),
      }));

    const itemsToNarrate = narrationTargets.filter((item) => !isPlayableNarrationAudio(item.chosenExplanationAudio));

    if (audioToPlay.length === 0 && itemsToNarrate.length === 0) {
      setCurrentNarrationTerms([]);
      setIsNarrationPaused(false);
      setIsNarrationCardPressed(false);
      restoreGlobalSelection();
      lastNarratedSubtitleRef.current = activeSubtitleId;
      return;
    }

    if (!episodeId || !session?.access_token) {
      console.warn('Narration requires authenticated session and episode id');
      setCurrentNarrationTerms([]);
      setIsNarrationPaused(false);
      setIsNarrationCardPressed(false);
      restoreGlobalSelection();
      lastNarratedSubtitleRef.current = activeSubtitleId;
      return;
    }

    narrationInFlightRef.current = true;
    setIsNarrationProcessing(true);

    const releasePlaybackLock = acquirePlaybackLock('mobile-immersive-narration', { autoResume: false });
    narrationLockReleaseRef.current = releasePlaybackLock; // 保存到 ref 中
    let lockReleased = false;
    const safelyReleaseLock = () => {
      if (lockReleased) {
        return;
      }
      lockReleased = true;
      releasePlaybackLock();
      narrationLockReleaseRef.current = null; // 释放后清空 ref
    };

    const finalizeNarrationFlow = (shouldResume: boolean) => {
      narrationInFlightRef.current = false;
      setIsNarrationProcessing(false);
      lastNarratedSubtitleRef.current = activeSubtitleId;
      setCurrentNarrationTerms([]);
      setIsNarrationPaused(false);
      setIsNarrationCardPressed(false);
      restoreGlobalSelection();
      safelyReleaseLock();
      if (shouldResume) {
        try {
          play();
        } catch (error) {
          console.warn('Failed to resume player after narration:', error);
          shouldResumePlaybackRef.current = false;
        }
      } else {
        shouldResumePlaybackRef.current = false;
      }
    };

    const generateNarration = async () => {
      try {
        if (!narrationEnabledRef.current || itemsToNarrate.length === 0) {
          return [] as NarrationAudioQueueItem[];
        }

        const requestBody = {
          items: itemsToNarrate.map((item) => ({
            scenarioIndex: item.scenarioIndex,
            termIndex: item.termIndex,
            term: item.term,
            chosen_explanation: item.chosenExplanation,
          })),
        };

        // Try OpenAI first
        let response = await fetch(`/api/episodes/${episodeId}/vocabulary/openai-tts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify(requestBody),
        });

        // If OpenAI fails, fallback to Gemini (check for 429 or 500)
        if (!response.ok && (response.status === 429 || response.status === 500)) {
          response = await fetch(`/api/episodes/${episodeId}/vocabulary/chosen-explanation-audio`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify(requestBody),
          });
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
          throw new Error(`Narration request failed: ${response.status} - ${errorData.error || response.statusText}`);
        }

        const result = await response.json();

        if (result?.vocabulary && onVocabularyUpdate) {
          onVocabularyUpdate(result.vocabulary);
        }

        const itemsByPosition = new Map<string, { term: string }>();
        const fallbackQueue: { term: string }[] = [];

        for (const item of itemsToNarrate) {
          const hasPosition =
            typeof item.scenarioIndex === 'number' && typeof item.termIndex === 'number';

          if (hasPosition) {
            itemsByPosition.set(`${item.scenarioIndex}:${item.termIndex}`, { term: item.term });
          } else {
            fallbackQueue.push({ term: item.term });
          }
        }

        const parsedAudios = Array.isArray(result?.audios)
          ? (result.audios as Array<{ url?: string; scenarioIndex?: number; termIndex?: number }>)
          : [];

        const audioItems: NarrationAudioQueueItem[] = parsedAudios
          .map<NarrationAudioQueueItem | null>((item) => {
            const url = item?.url;
            if (typeof url !== 'string' || url.length === 0) {
              return null;
            }

            let resolvedTerm: string | undefined;

            if (typeof item?.scenarioIndex === 'number' && typeof item?.termIndex === 'number') {
              const key = `${item.scenarioIndex}:${item.termIndex}`;
              const match = itemsByPosition.get(key);
              if (match) {
                resolvedTerm = match.term;
                itemsByPosition.delete(key);
              }
            }

            if (!resolvedTerm && fallbackQueue.length > 0) {
              const fallback = fallbackQueue.shift();
              resolvedTerm = fallback?.term;
            }

            return {
              term: resolvedTerm,
              url,
            };
          })
          .filter((item): item is NarrationAudioQueueItem => Boolean(item));

        return audioItems;
      } catch (error) {
        if ((error as any)?.name === 'AbortError') {
          console.warn('Narration request aborted');
        } else {
          console.error('Failed to generate narration audio:', error);
        }
        return [] as NarrationAudioQueueItem[];
      } finally {
        setIsNarrationProcessing(false);
        narrationInFlightRef.current = false;
        lastNarratedSubtitleRef.current = activeSubtitleId;
      }
    };

    const runNarrationFlow = async () => {
      // 1. 先暂停视频
      console.log('⏸️ 暂停视频以播放讲解');
      try {
        pause();
        // 在移动端等待一小段时间确保暂停生效
        await new Promise(resolve => setTimeout(resolve, 150));
      } catch (error) {
        console.warn('Failed to pause player before narration:', error);
      }

      await primeNarrationAudioElement();

      let hasPlayedAnyAudio = false;

      try {
        // 2. 播放已存在的音频
        if (audioToPlay.length > 0 && narrationEnabledRef.current) {
          console.log('🎵 播放已存在的音频:', audioToPlay.length);
          const playedExisting = await playNarrationQueue(audioToPlay);
          hasPlayedAnyAudio = hasPlayedAnyAudio || playedExisting;
        }

        // 3. 生成并播放新音频
        const generatedAudio = await generateNarration();

        if (generatedAudio.length > 0 && narrationEnabledRef.current) {
          console.log('🎵 播放新生成的音频:', generatedAudio.length);
          const playedGenerated = await playNarrationQueue(generatedAudio);
          hasPlayedAnyAudio = hasPlayedAnyAudio || playedGenerated;
        } else if (generatedAudio.length === 0 && audioToPlay.length === 0) {
          console.log('⚠️ 没有任何音频可播放');
        }
      } finally {
        // 4. 只有在真正播放了音频后才恢复播放
        const shouldResume = narrationEnabledRef.current && hasPlayedAnyAudio;
        console.log('🎬 讲解流程结束，是否恢复播放:', shouldResume, '(played:', hasPlayedAnyAudio, ')');
        finalizeNarrationFlow(shouldResume);
      }
    };

    void runNarrationFlow().catch(error => {
      console.error('Narration flow failed:', error);
    });
  }, [
    narrationEnabled,
    sentence,
    subtitleTimestamp,
    currentTime,
    activeSubtitleId,
    activeVocabularyTerms,
    findVocabularyTerm,
    episodeId,
    session?.access_token,
    onVocabularyUpdate,
    pause,
    play,
    acquirePlaybackLock,
    primeNarrationAudioElement,
    playNarrationQueue,
    restoreGlobalSelection,
  ]);

  useEffect(() => {
    if (!shadowDialogOpen) {
      setShowWordDrawer(false);
    }
  }, [shadowDialogOpen]);

  const handleWordClick = useCallback(async (word: string) => {
    const trimmed = word.trim();
    if (!trimmed) {
      return;
    }

    try {
      const { doSpeak } = await import("@/components/tts");
      const controller = new AbortController();

      await doSpeak({
        provider: "EdgeTTS",
        text: trimmed,
        lang: "en",
        rate: 10,
        volume: 100,
        signal: controller.signal,
      });
    } catch (error) {
      console.error('Failed to speak word', error);
    }
  }, []);

  const interactiveSentence = useMemo<React.ReactNode[]>(() => {
    if (!sentence) {
      return [];
    }

    const tokens = sentence.split(/(\s+|[^\w\s]+)/);

    return tokens.map((token, index) => {
      const trimmed = token.trim();
      const isWord = /\b[a-zA-Z]+(?:'[a-zA-Z]+)?\b/.test(trimmed);

      if (!isWord) {
        return <React.Fragment key={`token-${index}`}>{token}</React.Fragment>;
      }

      return (
        <span
          key={`token-${index}`}
          className="cursor-pointer rounded-lg px-1 py-0.5 transition hover:bg-primary/10 hover:text-primary"
          onClick={(event) => {
            event.stopPropagation();
            handleWordClick(trimmed);
          }}
          onDoubleClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void handleWordDrawerOpen(trimmed, sentence ?? "");
          }}
          onTouchEnd={(event) => {
            const now = Date.now();
            if (now - lastTapRef.current < 300) {
              event.preventDefault();
              event.stopPropagation();
              void handleWordDrawerOpen(trimmed, sentence ?? "");
            }
            lastTapRef.current = now;
          }}
        >
          {token}
        </span>
      );
    });
  }, [sentence, handleWordClick, handleWordDrawerOpen]);

  useEffect(() => {
    if (!shadowDialogOpen) {
      if (isRecording) {
        stopRecording();
      }
      setDialogShowSentence(true);
      setDialogShowTranslation(false);
    }
  }, [shadowDialogOpen, isRecording, stopRecording]);

  useEffect(() => {
    if (!shadowDialogOpen) {
      return;
    }

    try {
      pause();
    } catch (error) {
      console.warn('Failed to pause player when opening shadowing dialog:', error);
    }
  }, [shadowDialogOpen, pause]);

  useEffect(() => {
    if (wasDialogOpenRef.current && !shadowDialogOpen) {
      triggeredSubtitleIdRef.current = activeSubtitleId ?? triggeredSubtitleIdRef.current;
      if (shouldResumePlaybackRef.current) {
        try {
          play();
        } catch (error) {
          console.warn('Failed to resume player after closing shadowing dialog:', error);
        }
      }
      shouldResumePlaybackRef.current = true;
    }
    wasDialogOpenRef.current = shadowDialogOpen;
  }, [shadowDialogOpen, play, activeSubtitleId]);

  useEffect(() => {
    if (!shadowPracticeMode) {
      return;
    }

    previousSubtitleIdRef.current = activeSubtitleId ?? null;
    triggeredSubtitleIdRef.current = null;
    resetRecorder();
    setAttemptCount(0);
    shouldResumePlaybackRef.current = true;
    if (!hasSentence) {
      setShadowDialogOpen(false);
    }
  }, [shadowPracticeMode, activeSubtitleId, hasSentence, resetRecorder]);

  useEffect(() => {
    if (shadowPracticeMode) {
      return;
    }

    previousSubtitleIdRef.current = activeSubtitleId ?? null;

    if (!manualShadowing) {
      setShadowDialogOpen(false);
      triggeredSubtitleIdRef.current = null;
      shouldResumePlaybackRef.current = true;
      if (isRecording) {
        stopRecording();
      }
    }
  }, [shadowPracticeMode, manualShadowing, activeSubtitleId, isRecording, stopRecording]);

  useEffect(() => {
    if (!shadowPracticeMode) {
      previousSubtitleIdRef.current = activeSubtitleId ?? null;
      triggeredSubtitleIdRef.current = null;
      return;
    }

    const currentId = activeSubtitleId ?? null;
    if (previousSubtitleIdRef.current !== currentId) {
      previousSubtitleIdRef.current = currentId;
      triggeredSubtitleIdRef.current = null;

      if (!subtitleTimestamp && hasSentence && currentId) {
        triggeredSubtitleIdRef.current = currentId;
        shouldResumePlaybackRef.current = true;
        setShadowDialogOpen(true);
      }
    }
  }, [activeSubtitleId, shadowPracticeMode, hasSentence, subtitleTimestamp]);

  useEffect(() => {
    if (
      !shadowPracticeMode ||
      !subtitleTimestamp ||
      !activeSubtitleId ||
      !hasSentence ||
      typeof currentTime !== 'number'
    ) {
      return;
    }

    const [start, end] = subtitleTimestamp;
    if (!Number.isFinite(end)) {
      return;
    }

    const offset = 0.25;
    const triggerPoint = Math.max(start, end - offset);

    if (currentTime >= triggerPoint && triggeredSubtitleIdRef.current !== activeSubtitleId) {
      triggeredSubtitleIdRef.current = activeSubtitleId;
      shouldResumePlaybackRef.current = true;
      setShadowDialogOpen(true);
    }
  }, [
    currentTime,
    shadowPracticeMode,
    subtitleTimestamp,
    activeSubtitleId,
    hasSentence,
  ]);

  const handleManualShadowClick = () => {
    if (!hasSentence || isTranscribing) {
      return;
    }

    if (shadowPracticeMode) {
      setShadowPracticeMode(false);
    }

    if (isRecording) {
      stopRecording();
    }

    resetRecorder();
    setAttemptCount(0);
    const currentId = activeSubtitleId ?? null;
    triggeredSubtitleIdRef.current = currentId;
    manualSubtitleIdRef.current = currentId;
    shouldResumePlaybackRef.current = false;
    setManualShadowing(true);
    setShadowDialogOpen(true);
  };

  const handleRecordClick = async () => {
    if (isTranscribing) {
      return;
    }

    if (isRecording) {
      stopRecording();
    } else {
      await startRecording();
      startTimeRef.current = Date.now();
      setAttemptCount((prev) => prev + 1);
    }
  };

  useEffect(() => {
    const saveShadowingRecord = async () => {
      if (!transcribedText || !user || !sentence) return;

      const cleanedSentence = sentence.trim();
      if (!cleanedSentence) return;

      const accuracy =
        shadowingAccuracy ??
        calculateOptimalSimilarity(cleanedSentence, transcribedText);
      const durationSec = startTimeRef.current
        ? Math.max(1, Math.round((Date.now() - startTimeRef.current) / 1000))
        : 0;

      const segments = calculateTextDiff(cleanedSentence, transcribedText);
      const missing = segments
        .filter((segment) => segment.type === DiffType.DELETE)
        .map((segment: any) => segment.text || segment.originalText || "")
        .filter(Boolean);
      const wrong = segments
        .filter((segment) => segment.type === DiffType.SUBSTITUTE)
        .map((segment: any) => `${segment.originalText ?? ''} → ${segment.text ?? ''}`.trim())
        .filter(Boolean);
      const extra = segments
        .filter((segment) => segment.type === DiffType.INSERT)
        .map((segment: any) => segment.text || "")
        .filter(Boolean);

      const detailedMissing: Array<{ word: string; originalPosition: number }> = [];
      const detailedWrong: Array<{ originalWord: string; userWord: string; originalPosition: number; userPosition: number }> = [];
      const detailedExtra: Array<{ word: string; userPosition: number }> = [];
      let originalPosition = 0;
      let userPosition = 0;
      segments.forEach((segment: any) => {
        switch (segment.type) {
          case DiffType.DELETE:
            detailedMissing.push({ word: segment.text, originalPosition });
            originalPosition++;
            break;
          case DiffType.INSERT:
            detailedExtra.push({ word: segment.text, userPosition });
            userPosition++;
            break;
          case DiffType.SUBSTITUTE:
            detailedWrong.push({
              originalWord: segment.originalText,
              userWord: segment.text,
              originalPosition,
              userPosition,
            });
            originalPosition++;
            userPosition++;
            break;
          case DiffType.EQUAL:
            originalPosition++;
            userPosition++;
            break;
        }
      });

      const resolvedEpisodeId = episodeId && episodeId.trim().length > 0 ? episodeId : 'episode:unknown';
      const recordTitle = episodeTitle || t('mobileImmersive.shadowing.unknownEpisode', 'Unknown Episode');

      try {
        const res = await fetch('/api/shadowing-stats', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: user.id,
            record: {
              episodeId: resolvedEpisodeId,
              episodeTitle: recordTitle,
              episodeThumbnail,
              sentenceText: cleanedSentence,
              userTranscription: transcribedText,
              accuracy,
              duration: durationSec,
              attemptNumber: attemptCount + 1,
              subtitleId: activeSubtitleId ?? undefined,
              mistakes: {
                missing,
                wrong,
                extra,
                detailedMistakes: {
                  missing: detailedMissing,
                  wrong: detailedWrong,
                  extra: detailedExtra,
                },
              },
            },
          }),
        });

        if (res.ok) {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent('shadowingRecordSaved', {
                detail: { episodeId: resolvedEpisodeId, accuracy },
              })
            );
          }, 300);
        }
      } catch (error) {
        console.error('Failed to save shadowing record:', error);
      } finally {
        startTimeRef.current = null;
      }
    };

    void saveShadowingRecord();

  }, [transcribedText, user, sentence, episodeId, episodeTitle, episodeThumbnail, attemptCount, activeSubtitleId, t, shadowingAccuracy]);

  const [isTranslating, setIsTranslating] = useState(false);

  // Check for missing translations and auto-trigger if needed
  useEffect(() => {
    if (!showTranslation || !episodeId || !userSettings?.languageSetting) return;

    const lang = userSettings.languageSetting.split('-')[0].toLowerCase();
    const needsTranslation = (() => {
        if (!translation) return false; // No translation data at all
        if (typeof translation === 'string') return lang !== 'zh'; // Assuming string is Chinese
        if (typeof translation === 'object') {
            const hasKey = !!translation[lang];
            return !hasKey;
        }
        return false;
    })();

    if (needsTranslation) {
      const triggerTranslation = async () => {
        setIsTranslating(true);
        try {
            const langMap: Record<string, string> = {
                'zh': 'Chinese',
                'en': 'English',
                'ja': 'Japanese',
                'ko': 'Korean',
            };
            
            const response = await fetch(`/api/episodes/${episodeId}/translate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    targetLanguage: langMap[lang] || 'Chinese'
                })
            });

            if (response.ok) {
                const result = await response.json();
                if (result.success && result.translatedTranscript) {
                    // Notify parent to update data via existing mechanism (e.g. onTranscriptUpdate if available, or re-fetch)
                    // Note: MobileImmersiveFloatingButtons doesn't have onTranscriptUpdate prop directly, 
                    // but the parent usually handles updates.
                    // However, this component receives `translation` as a prop. 
                    // If the parent doesn't update, this local effect won't help much unless we can trigger a refresh.
                    // Assuming parent listens to some update or we need to notify parent.
                    // For now, we just handle the UI state.
                }
            }
        } catch (e) {
            console.error("[MobileImmersive] Failed to trigger translation补全", e);
        } finally {
            setIsTranslating(false);
        }
      };

      // Debounce slightly to avoid rapid toggles
      const timer = setTimeout(triggerTranslation, 1000);
      return () => clearTimeout(timer);
    }
  }, [showTranslation, translation, episodeId, userSettings]);

  const translationToggleLabel = allTranslationsRevealed
    ? t('mobileImmersive.translationReveal.hideAll', 'Hide all vocabulary translations')
    : t('mobileImmersive.translationReveal.showAll', 'Reveal all vocabulary translations');

  const collapseToggleLabel = isCollapsed
    ? t('mobileImmersive.controls.expand', 'Show quick controls')
    : t('mobileImmersive.controls.collapse', 'Hide quick controls');

  const renderManualButton = (
    <div className={cn(MANUAL_BUTTON_WRAPPER_CLASS, 'flex flex-col items-end gap-2')}>
      {!isCollapsed && (
        <>
          {/* <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    TEXT_TOGGLE_BUTTON_CLASS,
                    manualShadowing
                      ? TEXT_TOGGLE_ACTIVE_CLASS
                      : TEXT_TOGGLE_INACTIVE_CLASS,
                    !hasSentence && 'opacity-60'
                  )}
                  onClick={handleManualShadowClick}
                  disabled={!hasSentence}
                  aria-label={t('mobileImmersive.shadowing.manualTooltip', 'Practice current subtitle now')}
                  aria-pressed={manualShadowing}
                >
                  <Mic className="h-6 w-6" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>{t('mobileImmersive.shadowing.manualTooltip', 'Practice current subtitle now')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider> */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    TEXT_TOGGLE_BUTTON_CLASS,
                    allTranslationsRevealed ? TEXT_TOGGLE_ACTIVE_CLASS : TEXT_TOGGLE_INACTIVE_CLASS,
                    'h-9 w-9'
                  )}
                  onClick={() => setAllTranslationsRevealed(!allTranslationsRevealed)}
                  aria-pressed={allTranslationsRevealed}
                  aria-label={translationToggleLabel}
                >
                  {allTranslationsRevealed ? <Eye className="h-6 w-6" /> : <EyeOff className="h-6 w-6" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>{translationToggleLabel}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </>
      )}
      {isCollapsed && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={COLLAPSE_BUTTON_CLASS}
                onClick={() => setIsCollapsed(false)}
                aria-pressed={!isCollapsed}
                aria-label={collapseToggleLabel}
              >
                <Plus className="h-5 w-5" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p>{collapseToggleLabel}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );

  return (
    <>
      {/* Narration Mode Indicator */}
      {hasNarrationFeatureAccess && (
        <AnimatePresence>
          {isNarrationPlaying && primaryNarrationTerm && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: -88 }}
              exit={{ opacity: 0, scale: 0.9, y: -88 }}
              transition={{ duration: 0.3 }}
              className="pointer-events-none fixed inset-0 z-[60] flex items-center justify-center"
            >
              <div
                className={cn(
                  'pointer-events-auto relative rounded-[40px] transition-all duration-200',
                  narrationCardSurfaceClass,
                  narrationCardContentFadeClass
                )}
              >
                <NarrationModeIndicatorCard
                  term={primaryNarrationTerm}
                  isPlaying={!isNarrationPaused}
                  controlsDisabled={isNarrationProcessing}
                  onSkip={skipNarration}
                  onTogglePlayPause={handleNarrationPlaybackToggle}
                  onReplay={handleNarrationReplay}
                />
                <button
                  type="button"
                  aria-label="Reveal narration card"
                  className="absolute bottom-4 right-4 h-20 w-20 rounded-full opacity-0 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  onPointerDown={(event) => {
                    if (event.pointerType !== 'mouse') {
                      event.preventDefault();
                    }
                    event.stopPropagation();
                    disableGlobalSelection();
                    setIsNarrationCardPressed(true);
                  }}
                  onPointerUp={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsNarrationCardPressed(false);
                    restoreGlobalSelection();
                  }}
                  onPointerLeave={() => {
                    setIsNarrationCardPressed(false);
                    restoreGlobalSelection();
                  }}
                  onPointerCancel={() => {
                    setIsNarrationCardPressed(false);
                    restoreGlobalSelection();
                  }}
                  onTouchStart={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    disableGlobalSelection();
                    setIsNarrationCardPressed(true);
                  }}
                  onTouchEnd={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setIsNarrationCardPressed(false);
                    restoreGlobalSelection();
                  }}
                  onTouchCancel={() => {
                    setIsNarrationCardPressed(false);
                    restoreGlobalSelection();
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      )}

      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className={PANEL_WRAPPER_CLASS}
          >
            <div className={PANEL_INNER_CLASS}>
            <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        ICON_BUTTON_BASE_CLASS,
                        ICON_BUTTON_SUBTLE_CLASS,
                        ICON_BUTTON_INACTIVE_CLASS
                      )}
                      onClick={() => setIsCollapsed(true)}
                      aria-label={collapseToggleLabel}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p>{t('mobileImmersive.controls.collapseTooltip', 'Hide quick controls')}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {/* Subtitle toggle button */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        ICON_BUTTON_BASE_CLASS,
                        ICON_BUTTON_SUBTLE_CLASS,
                        showSubtitles ? ICON_BUTTON_ACTIVE_CLASS : ICON_BUTTON_INACTIVE_CLASS
                      )}
                      onClick={() => setShowSubtitles(!showSubtitles)}
                    >
                      <Subtitles
                        size={14}
                        className={showSubtitles ? "text-primary-foreground" : undefined}
                      />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p>
                      {showSubtitles
                        ? t('mobileImmersive.subtitles.hide', 'Hide subtitles')
                        : t('mobileImmersive.subtitles.show', 'Show subtitles')}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              {/* Translation toggle button */}
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  TEXT_TOGGLE_BUTTON_CLASS,
                  showTranslation ? TEXT_TOGGLE_ACTIVE_CLASS : TEXT_TOGGLE_INACTIVE_CLASS,
                  lang === 'chinese' && 'font-normal'
                )}
                aria-pressed={showTranslation}
                aria-label={showTranslation
                  ? t('mobileImmersive.translation.hide', 'Hide translation')
                  : t('mobileImmersive.translation.show', 'Show translation')}
                onClick={() => setShowTranslation(!showTranslation)}
              >
                {isTranslating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                    <Languages className="h-4 w-4" />
                )}
              </Button>

              {hasNarrationFeatureAccess && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className={cn(
                          ICON_BUTTON_BASE_CLASS,
                          ICON_BUTTON_SUBTLE_CLASS,
                          narrationEnabled
                            ? ICON_BUTTON_ACTIVE_CLASS
                            : ICON_BUTTON_INACTIVE_CLASS
                        )}
                        aria-pressed={narrationEnabled}
                        aria-label={
                          narrationEnabled
                            ? t('mobileImmersive.narration.disable', 'Disable narration')
                            : t('mobileImmersive.narration.enable', 'Enable narration')
                        }
                        onClick={() => {
                          void handleNarrationToggle();
                        }}
                      >
                        {isNarrationProcessing ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <MessageCircle className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="left">
                      <p>
                        {narrationEnabled
                          ? t('mobileImmersive.narration.tooltipDisable', 'Disable term narration')
                          : t('mobileImmersive.narration.tooltipEnable', 'Explain highlighted terms after playback')}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className={cn(
                        TEXT_TOGGLE_BUTTON_CLASS,
                        shadowPracticeMode
                          ? TEXT_TOGGLE_ACTIVE_CLASS
                          : TEXT_TOGGLE_INACTIVE_CLASS
                      )}
                      onClick={() => {
                        setManualShadowing(false);
                        manualSubtitleIdRef.current = null;
                        setShadowPracticeMode((prev) => !prev);
                      }}
                      aria-label={shadowPracticeMode
                        ? t('mobileImmersive.shadowing.disableTooltip', 'Disable auto shadowing practice')
                        : t('mobileImmersive.shadowing.tooltip', 'Enable auto shadowing practice')}
                      aria-pressed={shadowPracticeMode}
                    >
                      <Speech className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    <p>
                      {shadowPracticeMode
                        ? t('mobileImmersive.shadowing.disableTooltip', 'Disable auto shadowing practice')
                        : t('mobileImmersive.shadowing.tooltip', 'Enable auto shadowing practice')}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <Dialog
        open={shadowDialogOpen}
        onOpenChange={(open) => {
          setShadowDialogOpen(open);
          if (!open) {
            triggeredSubtitleIdRef.current = activeSubtitleId ?? triggeredSubtitleIdRef.current;
            setManualShadowing(false);
            manualSubtitleIdRef.current = null;
          }
        }}
      >
        <DialogContent className="max-w-sm rounded-3xl -mt-10 sm:-mt-20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Speech className="h-5 w-5" /> {t('mobileImmersive.shadowing.dialogTitle', 'Shadowing Practice')}
            </DialogTitle>
            <DialogDescription className="text-[12px] sm:text-sm text-start text-muted-foreground">
              {t('mobileImmersive.shadowing.dialogDescription', 'Repeat the current subtitle aloud.')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-muted-foreground">
                {t('mobileImmersive.shadowing.attemptLabel', 'Attempt')}: {attemptCount}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-7 sm:h-8 px-2 border rounded-lg sm:rounded-xl${lang === 'chinese' ? ' font-normal' : ''}`}
                  onClick={() => setDialogShowSentence((v) => !v)}
                  disabled={!sentence}
                >
                  {dialogShowSentence ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                  {dialogShowSentence
                    ? t('mobileImmersive.shadowing.hideText', 'Hide text')
                    : t('mobileImmersive.shadowing.showText', 'Show text')}
                </Button>
                {displayedTranslation && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-7 sm:h-8 px-2 border rounded-lg sm:rounded-xl${lang === 'chinese' ? ' font-normal' : ''}`}
                    onClick={() => setDialogShowTranslation((v) => !v)}
                  >
                    <Languages className="h-4 w-4 mr-1" />
                    {dialogShowTranslation
                      ? t('mobileImmersive.shadowing.hideTranslationShort', 'Hide translation')
                      : t('mobileImmersive.shadowing.showTranslationShort', 'Show translation')}
                  </Button>
                )}
              </div>
            </div>

            {dialogShowSentence && sentence && (
              <p className="text-md font-medium leading-relaxed text-foreground">
                {interactiveSentence}
              </p>
            )}
            {dialogShowTranslation && displayedTranslation && (
              <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">
                {displayedTranslation}
              </p>
            )}

            {transcribedText && sentence ? (
              <TextDiffHighlight
                originalText={sentence}
                userTranscription={transcribedText}
                accuracy={shadowingAccuracy ?? 0}
                renderScore={(score) => (
                  <ChartRadialText
                    variant="minimal"
                    value={score}
                    max={100}
                    centerLabel={t('mobileImmersive.shadowing.scoreLabel', 'Score')}
                    className="min-w-[96px] gap-0"
                    chartClassName="h-16 w-16"
                    startAngle={90}
                    endAngle={-270}
                  />
                )}
              />
            ) : transcribedText ? (
              <div className="rounded-xl border border-border/60 bg-background/70 p-3 space-y-1">
                <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('mobileImmersive.shadowing.transcriptionLabel', 'Your transcription')}
                </div>
                <p className="text-sm text-foreground leading-relaxed">{transcribedText}</p>
              </div>
            ) : null}

            <div className="flex flex-col items-center gap-2">
              {audioUrl && (
                <div className="w-full">
                  <AudioWave url={audioUrl} dark={false} autoPlay={false} />
                </div>
              )}

              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleRecordClick}
                      disabled={isTranscribing}
                      aria-label={isRecording
                        ? t('mobileImmersive.shadowing.stopRecording', 'Stop recording')
                        : t('mobileImmersive.shadowing.startRecording', 'Start shadowing')}
                      aria-pressed={isRecording}
                      aria-busy={isTranscribing}
                      className={cn(
                        'w-16 h-16 border p-2 rounded-full inline-flex items-center justify-center transition focus:outline-none focus-visible:outline-2 focus-visible:outline-primary/60 focus-visible:outline-offset-2',
                        isTranscribing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                        transcriptionError ? 'bg-red-100 hover:bg-red-200' : 'bg-background'
                      )}
                      style={{
                        boxSizing: 'border-box',
                        transition: isRecording ? 'background-color 0.2s ease' : 'box-shadow 0.05s linear, background-color 0.2s ease',
                        boxShadow: isRecording
                          ? `0 0 0 ${Math.min(6, effectiveLevel * 6)}px rgba(0, 0, 0, ${Math.max(0.05, Math.min(0.45, effectiveLevel * 0.45))})`
                          : '0 0 0 0px rgba(0, 0, 0, 0)'
                      }}
                    >
                      {!isRecording && !isTranscribing && !transcriptionError && (
                        <svg
                          className="w-full h-full text-black/80 dark:text-white"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth="1.5"
                          stroke="currentColor"
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 0 0 6-6v-1.5m-6 7.5a6 6 0 0 1-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 0 1-3-3V4.5a3 3 0 1 1 6 0v8.25a3 3 0 0 1-3 3Z" />
                        </svg>
                      )}
                      {isRecording && (
                        <svg className="w-full h-full text-red-500" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                      )}
                      {isTranscribing && (
                        <svg
                          className="w-full h-full text-black/80 dark:text-white animate-spin"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M12 2v4" />
                          <path d="m16.2 7.8 2.9-2.9" />
                          <path d="M18 12h4" />
                          <path d="m16.2 16.2 2.9 2.9" />
                          <path d="M12 18v4" />
                          <path d="m4.9 19.1 2.9-2.9" />
                          <path d="M2 12h4" />
                          <path d="m4.9 4.9 2.9 2.9" />
                        </svg>
                      )}
                      {transcriptionError && !isRecording && !isTranscribing && (
                        <svg
                          className="w-full h-full text-red-600"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="4"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <line x1="12" x2="12" y1="4" y2="14" />
                          <line x1="12" x2="12.01" y1="20" y2="20" />
                        </svg>
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {isRecording
                        ? t('mobileImmersive.shadowing.stop', 'Stop shadowing')
                        : t('mobileImmersive.shadowing.start', 'Start shadowing')}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

            </div>
          </div>
        </DialogContent>
      </Dialog>
      {selectedWord && (
        <Drawer
          open={showWordDrawer}
          onOpenChange={(open) => {
            setShowWordDrawer(open);
            if (!open) {
              setSelectedWord("");
              setSelectedWordContext("");
              setSelectedStatusValue("NEW");
            }
          }}
        >
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle className="flex items-center space-x-2">
                <GraduationCap className="h-6 w-6" />
                <span className="text-2xl font-bold">{selectedWord}</span>
              </DrawerTitle>
              {(wordExplanation?.usphone || wordTranslation?.usphone || wordTranslation?.phonetic) && (
                <DrawerDescription className="text-base text-start pl-8 font-medium text-stone-700">
                  /{wordExplanation?.usphone || wordTranslation?.usphone || wordTranslation?.phonetic}/
                </DrawerDescription>
              )}
            </DrawerHeader>

            <div className="px-4 pb-4">
              {isWordLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  <span className="ml-2 text-gray-600">Loading...</span>
                </div>
              ) : wordExplanation || wordTranslation ? (
                <div className="space-y-4">
                  {(wordExplanation?.translation || wordTranslation?.translation || wordTranslation?.explains?.[0]) && (
                    <div className="flex items-start space-x-3">
                      <MessageCircle className="h-5 w-5 text-gray-600 flex-shrink-0 mt-0.5" />
                      <div className="text-md text-foreground">
                        {wordExplanation?.translation
                          ? (Array.isArray(wordExplanation.translation)
                              ? wordExplanation.translation.map((part: string, index: number) => (
                                  <div key={index} className={index > 0 ? "mt-2" : ""}>{part.trim()}</div>
                                ))
                              : (wordExplanation.translation as string).split(';').map((part, index) => (
                                  <div key={index} className={index > 0 ? "mt-2" : ""}>{part.trim()}</div>
                                )))
                          : (wordTranslation?.translation || wordTranslation?.explains?.[0])}
                      </div>
                    </div>
                  )}

                  {wordTranslation?.explains && (wordTranslation.explains.length || 0) > 1 && (
                    <div className="flex items-start space-x-3 pt-3 border-t border-gray-100">
                      <Quote className="h-5 w-5 text-gray-600 mt-1 flex-shrink-0" />
                      <div className="text-sm text-gray-600">
                        {wordTranslation.explains.slice(1).map((explain, index) => (
                          <div key={index} className={index > 0 ? "mt-1" : ""}>
                            {explain}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {wordExplanation?.example && (
                    <div className="flex items-start space-x-3 pt-3 border-t border-gray-100">
                      <Quote className="h-5 w-5 text-gray-600 mt-1 flex-shrink-0" />
                      <span className="text-base text-gray-600 italic">
                        {wordExplanation.example}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  {t('vocabulary.noTranslation', 'No translation available')}
                </div>
              )}
            </div>

            <DrawerFooter>
              <div className="flex w-full items-stretch gap-1.5">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        'flex-1 min-w-0 justify-between rounded-xl border text-xs h-9 px-3',
                        statusStyles[selectedStatusMobile.value]
                      )}
                    >
                      <span className="flex items-center gap-2">
                        <StatusIconMobile className="h-3.5 w-3.5" />
                        {selectedStatusMobile.label}
                      </span>
                      <span className={cn('h-2 w-2 rounded-full', statusAccentMap[selectedStatusMobile.value])} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {statusOptions.map((option) => {
                      const OptionIcon = option.value === 'MASTERED' ? Check : option.value === 'FUZZY' ? Lightbulb : Sparkles;
                      const isSelected = selectedStatusMobile.value === option.value;
                      return (
                        <DropdownMenuItem
                          key={option.value}
                          onSelect={(event) => {
                            event.preventDefault();
                            setSelectedStatusValue(option.value);
                          }}
                          className={cn('flex items-start gap-2', isSelected && 'bg-accent/50 text-foreground')}
                        >
                          <OptionIcon className={cn('h-4 w-4 mt-0.5', isSelected ? 'text-primary' : 'text-muted-foreground')} />
                          <span className="flex flex-col text-sm">
                            <span className="font-medium flex items-center gap-1">
                              {option.label}
                              {isSelected && <Check className="h-3 w-3" />}
                            </span>
                            <span className="text-xs text-muted-foreground">{option.description}</span>
                          </span>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  size="sm"
                  className="flex-1 min-w-0 gap-1.5 rounded-xl text-xs h-9 px-3"
                  disabled={
                    !userSettings?.myVocabularySetting ||
                    !selectedWord ||
                    isWordSaving ||
                    isWordSaved ||
                    (!wordExplanation && !wordTranslation)
                  }
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();

                    if (!selectedWord || isWordSaving || isWordSaved) {
                      return;
                    }

                    const options: any = {
                      episodeId,
                      contextText: selectedWordContext,
                      status: selectedStatusMobile.value,
                    };

                    if (episodeTitle) {
                      options.episodeTitle = episodeTitle;
                    }
                    if (episodeThumbnail) {
                      options.episodeThumbnail = episodeThumbnail;
                    }

                    if (!wordExplanation && wordTranslation) {
                      options.youdaoTranslation = {
                        translation: wordTranslation.translation,
                        phonetic: wordTranslation.phonetic,
                        usphone: wordTranslation.usphone,
                        explains: wordTranslation.explains,
                      };
                    }

                    saveCard(selectedWord, options);
                  }}
                >
                  {isWordSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isWordSaved ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <ClipboardCopy className="h-4 w-4" />
                  )}
                  <span>{isWordSaved ? t('common.saved', 'Saved') : t('common.save')}</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex-1 min-w-0 border rounded-xl text-xs h-9 px-3"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (speakerRef.current) {
                      speakerRef.current.click();
                    }
                  }}
                >
                  <SpeakerIcon
                    divRef={speakerRef}
                    size={9}
                    provider="EdgeTTS"
                    text={selectedWord || ""}
                    lang={getSpeakerLang(userSettings?.languageSetting || "english") as any}
                    voice={undefined}
                    rate={10}
                    volume={100}
                  />
                  <span className="ml-2">{t('common.speak')}</span>
                </Button>
              </div>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      )}
      {renderManualButton}
    </>
  );
};
